<div class="container min-h-full flex flex-col mx-auto px-4 md:px-6 pt-8 pb-4">
  <div class="flex items-center mb-4 min-h-10">
    <h2 class="text-2xl font-medium">
      {{
        activeDetail()?.label ||
          (activeAgent() === 'interaction_creator'
            ? 'Interactie-maker'
            : 'Interactie-auditor')
      }}
    </h2>
  </div>

  @if (activeAgent() === 'interaction_creator') {
    <div class="flex gap-3 rounded-lg border border-blue-200 bg-blue-50 p-4 mb-8">
      <mat-icon class="shrink-0 text-blue-600 mt-0.5">info</mat-icon>
      <div class="text-sm text-gray-800 space-y-1">
        <p class="font-medium text-gray-900">Over de interactie-maker</p>
        <p>
          De <strong>Interactie-maker</strong> ondersteunt zorgverleners bij het
          genereren en ontwerpen van zorgdialogen op sociale robots. Elk voorstel wordt
          vóór weergave automatisch getoetst door de interactie-auditor.
        </p>
      </div>
    </div>
  } @else {
    <div class="flex gap-3 rounded-lg border border-blue-200 bg-blue-50 p-4 mb-8">
      <mat-icon class="shrink-0 text-blue-600 mt-0.5">info</mat-icon>
      <div class="text-sm text-gray-800 space-y-1">
        <p class="font-medium text-gray-900">Over de interactie-auditor</p>
        <p>
          De <strong>Interactie-auditor</strong> toetst voorgestelde dialogen
          automatisch op taal, begrijpelijkheid, huisregels en medische veiligheid
          voordat een voorstel wordt aangeboden.
        </p>
      </div>
    </div>
  }

  @if (loading()) {
    <div class="flex items-center justify-center p-16">
      <mat-progress-spinner mode="indeterminate" diameter="48"></mat-progress-spinner>
    </div>
  } @else {
    @if (activeDetail(); as detail) {
      <form class="flex flex-col gap-12" [formGroup]="form">
        <!-- Model Selection Section -->
        <div class="grid lg:grid-cols-3 gap-4">
          <div>
            <div class="font-medium">Taalmodel (LLM)</div>
            <div class="text-sm text-pretty text-gray-700 mt-1">
              Selecteer welk AI-model gebruikt wordt voor deze agent uit de door het
              platform ondersteunde modellen.
            </div>
          </div>

          <div class="col-span-2 bg-white rounded-lg">
            <div class="p-8">
              <div class="grid grid-cols-6 gap-4 max-w-2xl">
                <div class="col-span-full lg:col-span-4">
                  <div class="space-y-2">
                    <div class="flex items-center justify-between">
                      <label
                        id="agentModelLabel"
                        class="block font-medium text-sm select-none"
                      >
                        Actief model
                      </label>
                      @if (activeControl().value !== detail.defaultModel) {
                        <button
                          type="button"
                          mat-button
                          color="primary"
                          class="text-xs!"
                          (click)="resetActiveToDefault()"
                        >
                          <mat-icon class="text-sm! size-4 leading-none mr-1"
                            >restart_alt</mat-icon
                          >
                          Herstellen naar standaard
                        </button>
                      }
                    </div>

                    <mat-form-field class="w-full">
                      <!-- aria-labelledby, not <label for>: a mat-select host is not a
                           labelable element, so `for` never focuses the control. -->
                      <mat-select
                        [formControl]="activeControl()"
                        id="agentModel"
                        aria-labelledby="agentModelLabel"
                        placeholder="Selecteer een model"
                      >
                        @for (model of availableModels(); track model.id) {
                          <mat-option [value]="model.id">
                            <div class="flex items-center justify-between w-full py-1">
                              <!-- The Scaleway model id verbatim, in mono to match
                                   the "Standaard:" line below: it is what an admin
                                   sees in the Scaleway console, so a prettified
                                   label would be one more thing to map by hand. -->
                              <span class="font-mono text-sm">{{ model.id }}</span>
                              @if (model.recommended) {
                                <span
                                  class="text-xs px-2 py-0.5 rounded-full bg-green-100 text-green-800 font-medium ml-2"
                                >
                                  Aanbevolen
                                </span>
                              }
                            </div>
                          </mat-option>
                        }
                      </mat-select>
                    </mat-form-field>

                    <div class="text-xs text-gray-500">
                      Standaard:
                      <span class="font-mono text-gray-700">{{
                        detail.defaultModel
                      }}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- System Prompt Section -->
        <div class="grid lg:grid-cols-3 gap-4">
          <div>
            <div class="font-medium">Systeemprompt</div>
            <div class="text-sm text-pretty text-gray-700 mt-1">
              De instructies en gedragsregels die het model aansturen. Dit is
              alleen-lezen zodat u exact kunt inzien hoe deze agent te werk gaat.
            </div>
          </div>

          <div class="col-span-2 bg-white rounded-lg">
            <div class="p-8">
              <div class="flex flex-col gap-3 max-w-3xl">
                <div>
                  <div class="text-sm font-medium text-gray-900">
                    Instructies (Alleen-lezen)
                  </div>
                  <div class="text-xs text-gray-500">
                    Gedefinieerd in de backend van de applicatie
                  </div>
                </div>

                <div class="bg-gray-50 rounded-lg border border-gray-200 p-4">
                  <pre
                    class="font-mono text-xs text-gray-800 leading-relaxed overflow-y-auto max-h-[400px] whitespace-pre-wrap select-all"
                    >{{ activeSystemPrompt() }}</pre
                  >
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Action Footer -->
        <div *appHasPermission="'ai_agent_settings:update'" class="flex justify-end">
          <button
            mat-flat-button
            type="button"
            [disabled]="!hasChanges() || saving()"
            (click)="saveSettings()"
          >
            @if (saving()) {
              <mat-progress-spinner
                mode="indeterminate"
                diameter="18"
                class="mr-2 inline-block align-middle"
              ></mat-progress-spinner>
            }
            Opslaan
          </button>
        </div>
      </form>
    }
  }
</div>
